import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { api, fmtMoney, formatApiError } from "../lib/api";
import { toast } from "sonner";

const EMPTY = {
  name: "", type: "Forestry", region: "", certifier: "Verra (VCS)", vintage: 2024,
  tons_available: 1000, price_usd_per_ton: 20, description: "", image_url: "",
};

export default function Admin() {
  const { t } = useTranslation();
  const tr = (k, o) => t(`admin_page.${k}`, o);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showUnlisted, setShowUnlisted] = useState(false);

  const load = async () => {
    setStats((await api.get("/admin/stats")).data);
    const url = showUnlisted ? "/projects?include_unlisted=true" : "/projects";
    setProjects((await api.get(url)).data || []);
  };
  useEffect(() => { load(); }, [showUnlisted]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, vintage: Number(form.vintage), tons_available: Number(form.tons_available), price_usd_per_ton: Number(form.price_usd_per_ton) };
      if (editId) {
        await api.put(`/projects/${editId}`, payload);
        toast.success("Project updated.");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created.");
      }
      setForm(EMPTY); setEditId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const edit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name, type: p.type, region: p.region, certifier: p.certifier, vintage: p.vintage,
      tons_available: p.tons_available, price_usd_per_ton: p.price_usd_per_ton,
      description: p.description, image_url: p.image_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm(tr("confirm_delete"))) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted");
    await load();
  };

  const exclude = async (p) => {
    const reason = window.prompt(tr("exclude_prompt", { name: p.name }), "");
    if (reason === null) return; // cancelled
    try {
      await api.post(`/projects/${p.id}/exclude`, { reason });
      toast.success(tr("excluded_toast", { name: p.name }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to exclude");
    }
  };

  const restore = async (p) => {
    try {
      await api.post(`/projects/${p.id}/restore`, {});
      toast.success(tr("restored_toast", { name: p.name }));
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to restore");
    }
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="label-tag mb-4">{tr("tag")}</div>
        <h1 className="font-serif text-5xl tracking-tight">{tr("title")}</h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-5 gap-px bg-line border border-line">
        <Stat label={tr("stat_users")} value={stats?.users ?? "—"} />
        <Stat label={tr("stat_projects")} value={stats?.projects ?? "—"} />
        <Stat label={tr("stat_trades")} value={stats?.transactions ?? "—"} />
        <Stat label={tr("stat_paid")} value={stats?.paid_deposits ?? "—"} />
        <Stat label={tr("stat_volume")} value={fmtMoney(stats?.trade_volume_usd ?? 0, "USD")} />
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <form onSubmit={save} className="lg:col-span-5 border border-ink p-6 space-y-4" data-testid="admin-form">
          <div className="label-tag">{editId ? tr("edit_project") : tr("create_project")}</div>
          <In label={tr("name")} id="name" form={form} setForm={setForm} />
          <div className="grid grid-cols-2 gap-3">
            <In label={tr("type")} id="type" form={form} setForm={setForm} />
            <In label={tr("region")} id="region" form={form} setForm={setForm} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <In label={tr("certifier")} id="certifier" form={form} setForm={setForm} />
            <In label={tr("vintage")} id="vintage" type="number" form={form} setForm={setForm} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <In label={tr("tons_available")} id="tons_available" type="number" form={form} setForm={setForm} />
            <In label={tr("price_per_ton")} id="price_usd_per_ton" type="number" step="0.01" form={form} setForm={setForm} />
          </div>
          <In label={tr("image_url")} id="image_url" form={form} setForm={setForm} required={false} />
          <div>
            <label className="label-tag block mb-2">{tr("description")}</label>
            <textarea rows={4} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="input-field" data-testid="admin-description" />
          </div>
          <div className="flex gap-2">
            {(() => {
              let label = busy ? tr("saving") : tr("create_project");
              if (!busy && editId) label = tr("update_project");
              return (
                <button type="submit" disabled={busy} className="btn-primary flex-1 disabled:opacity-50" data-testid="admin-save">
                  {label}
                </button>
              );
            })()}
            {editId && <button type="button" onClick={() => { setEditId(null); setForm(EMPTY); }} className="btn-outline">{tr("cancel")}</button>}
          </div>
        </form>

        <div className="lg:col-span-7 border border-line bg-surface overflow-x-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-line">
            <div className="label-tag">{tr("projects_count")} · {projects.length}</div>
            <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-ink2 cursor-pointer">
              <input
                type="checkbox"
                checked={showUnlisted}
                onChange={(e) => setShowUnlisted(e.target.checked)}
                data-testid="show-unlisted-toggle"
                className="h-3.5 w-3.5 accent-ink"
              />
              {tr("show_excluded")}
            </label>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <Th>{tr("col_name")}</Th><Th>{tr("col_type")}</Th><Th>{tr("col_avail")}</Th><Th>{tr("col_price")}</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr
                  key={p.id}
                  className={`border-b border-line/60 ${p.unlisted ? "bg-muted/60 text-ink2" : ""}`}
                  data-testid={`admin-row-${p.id}`}
                >
                  <Td>
                    <div className="flex items-center gap-2">
                      <span className={p.unlisted ? "line-through" : ""}>{p.name}</span>
                      {p.unlisted && (
                        <span
                          className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-danger/40 bg-danger/10 text-danger"
                          data-testid={`badge-excluded-${p.id}`}
                          title={p.unlisted_reason || tr("badge_excluded")}
                        >
                          {tr("badge_excluded")}
                        </span>
                      )}
                      {p.source === "producer" && (
                        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-leaf/40 bg-leaf/10 text-leaf">
                          {tr("badge_producer")}
                        </span>
                      )}
                    </div>
                    {p.unlisted && p.unlisted_reason && (
                      <div className="text-[10px] font-mono text-ink2 mt-1">{tr("reason")}: {p.unlisted_reason}</div>
                    )}
                  </Td>
                  <Td className="text-ink2">{p.type}</Td>
                  <Td className="mono-num">{p.tons_available.toFixed(0)}</Td>
                  <Td className="mono-num">{p.price_usd_per_ton.toFixed(2)}</Td>
                  <Td className="text-right whitespace-nowrap">
                    <button onClick={() => edit(p)} className="text-xs font-mono underline mr-3" data-testid={`edit-${p.id}`}>{tr("edit")}</button>
                    {p.unlisted ? (
                      <button onClick={() => restore(p)} className="text-xs font-mono text-leaf underline mr-3" data-testid={`restore-${p.id}`}>
                        {tr("restore")}
                      </button>
                    ) : (
                      <button onClick={() => exclude(p)} className="text-xs font-mono text-ink underline mr-3" data-testid={`exclude-${p.id}`}>
                        {tr("exclude")}
                      </button>
                    )}
                    <button onClick={() => remove(p.id)} className="text-xs font-mono text-danger underline" data-testid={`delete-${p.id}`}>{tr("delete")}</button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <div className="h-24" />
    </Layout>
  );
}

function Stat({ label, value }) {
  return <div className="bg-surface p-6"><div className="label-tag">{label}</div><div className="mono-num text-2xl mt-2">{value}</div></div>;
}
function In({ label, id, form, setForm, type = "text", required = true, step }) {
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      <input type={type} step={step} required={required} value={form[id]} onChange={(e) => setForm((f) => ({ ...f, [id]: e.target.value }))} className="input-field" data-testid={`admin-${id}`} />
    </div>
  );
}
function Th({ children }) { return <th className="px-4 py-3 text-[11px] font-sans tracking-[0.2em] uppercase text-ink2">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }
